//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";
// AutopilotForm imports
import { IProps, IState, IContainer } from "./AutopilotFormSaveOnEdit.d";
import { autopilotFormSaveOnEditFragments, refetchQuery } from "./AutopilotFormSaveOnEdit.gql";
import { AutopilotForm } from "./AutopilotForm";
// util
import { addAndRemoveOrgTags } from "@mutations";
import { eventHasRoom, getBaseExternalEventId, niceNameFromPerson } from "@util/event.util";
import { track, TrackingEvents } from "@util/analytics.util";
import {
  EFlex,
  getFlexFromEvent,
  getSimilarRoomsOkFromEvent,
  getTagState,
  SubjectTypeEnum,
  Tags,
  checkIfEventTagsChanged,
  generateEventFormSaveTags,
  maybeGetWarningType,
  ROOM_FLEXIBILITY_VALUES,
} from "@util/event-tag.util";
import {
  TagState,
  isAutopilotExplicitlyEnabled,
} from "@clockwise/client-commons/src/util/event-tag";
import { IAutopilotSuggestionOptions } from "@clockwise/client-commons/src/util/event-tag";
import { ZonedMoment } from "@util/time-zone.util";
import { getOrganizerNameAndEmailFromAttendeeList } from "@util/event.util";
import { PostMessageManager } from "@util/post-message.util";
import { getSetMoveRange } from "@clockwise/client-commons/src/util/event";
import { logger } from "@util/logger.util";
// mutations
import { unpauseEvent } from "@mutations";
// state
import { snackBarOpen } from "@actions/snack-bar.actions";
import { IReduxState } from "@state/reducers/root.reducer";
import { setCurrentTip } from "@actions/week-view.actions";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";
import { getValue } from "@src/util/errorable.util";
import TutorialSetMeetingFlexibility from "@components/tutorial-tip/tutorial-set-meeting-flexibility/TutorialSetMeetingFlexibility";

//////////////////
// COMPONENT
//////////////////
class AutopilotFormSaveOnEditCmpt extends React.Component<IProps, IState> {
  // ~-~-~-~-~-~-~-
  // Setup
  // ~-~-~-~-~-~-~-
  constructor(props: IProps) {
    super(props);

    const tagState = this.getMovementTagStateFromEvent(props.event);

    this.state = this.getStateFromProps(props);

    this.props.relay.refetch({
      calendarIds: this.getCalendarIdsFromEvent(this.props.event),
      userIds: this.getUserIdsFromTagState(tagState),
      orgRelayId: props.org.id,
    });
  }

  static contextType = TranslationContext;
  declare context: React.ContextType<typeof TranslationContext>;

  // ~-~-~-~-~-~-~-
  // Life-Cycle
  // ~-~-~-~-~-~-~-
  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const { activeViewEvent, setEventTags, eventParent, org, relay, calendarId } = nextProps;

    if (
      setEventTags.setEventTagsMillis > this.props.setEventTags.setEventTagsMillis &&
      activeViewEvent.externalEventId === eventParent.externalEventId &&
      setEventTags.externalEventId !== eventParent.externalEventId
    ) {
      logger.error(
        `Mismatch between setEventTags.externalEventId and eventParent.externalEventId`,
        activeViewEvent,
        setEventTags,
        eventParent,
        calendarId,
        {
          setEventTagExternalEventId: setEventTags.externalEventId,
          eventParentExternalEventId: eventParent.externalEventId,
        },
      );
    }

    if (
      setEventTags.setEventTagsMillis > this.props.setEventTags.setEventTagsMillis &&
      activeViewEvent.externalEventId === eventParent.externalEventId &&
      setEventTags.externalEventId === eventParent.externalEventId
    ) {
      // construct the tags we'll add or remove
      const saveTags = generateEventFormSaveTags(
        setEventTags.flex as EFlex,
        false,
        false,
        setEventTags.allowSimilarRooms === ROOM_FLEXIBILITY_VALUES.FLEXIBLE,
      );

      const moveRangeV3 = {
        allowedStartTimeInclusive: setEventTags.allowedStartV3 || undefined,
        allowedEndTimeInclusive: setEventTags.allowedEndV3 || undefined,
      };

      this.setState({
        isSaving: true,
        flex: setEventTags.flex as EFlex,
        allowSimilarRooms: setEventTags.allowSimilarRooms === ROOM_FLEXIBILITY_VALUES.FLEXIBLE,
        allowedStart: moveRangeV3.allowedStartTimeInclusive,
        allowedEnd: moveRangeV3.allowedEndTimeInclusive,
      });

      addAndRemoveOrgTags(
        relay.environment,
        {
          calendarId,
          orgRelayId: org.id,
          eventParentRelayId: eventParent.id,
          moveRangeV3,
          ...saveTags,
        },
        this.onSaveSuccess,
        this.onError,
        `APFormSaveOnEdit - componentWillReceiveProps - (setEventTagsMillis: ${setEventTags.setEventTagsMillis}, externalEventId: ${setEventTags.externalEventId}, calendarId: ${setEventTags.calendarId})`,
      );
    }

    const tagsChanged = checkIfEventTagsChanged(this.props.event, nextProps.event);
    if (tagsChanged) {
      const newState = this.getStateFromProps(nextProps);
      this.setState(newState);

      const tagState = this.getMovementTagStateFromEvent(this.props.event);
      const nextTagState = this.getMovementTagStateFromEvent(nextProps.event);

      if (tagState !== nextTagState) {
        this.props.relay.refetch({
          calendarIds: this.getCalendarIdsFromEvent(nextProps.event),
          userIds: this.getUserIdsFromTagState(nextTagState),
          orgRelayId: nextProps.org.id,
        });
      }
    }
  }

  public componentDidMount() {
    const {
      event,
      enableOnMount,
      setTimeFlexOnMount,
      setRoomFlexOnMount,
      canAutopilot,
    } = this.props;

    const flex = getFlexFromEvent(event, true);
    let allowSimilarRooms = getSimilarRoomsOkFromEvent(event);
    this.sendEventAutopilotStatus(this.props, flex, !!allowSimilarRooms);

    // enable on mount?
    if (
      ((enableOnMount && flex === EFlex.Pin) || // if ?apenable in url and not toggled on
        (setTimeFlexOnMount === EFlex.Week && flex !== EFlex.Week) || // if ?apflexweek in url and not already set to week
        (setTimeFlexOnMount === EFlex.Day && flex !== EFlex.Day) || // if ?apflexday in url and not already set to day
        (setRoomFlexOnMount && !allowSimilarRooms)) && // if ?apflexroom in url and not already allowing rooms to be enabled
      canAutopilot
    ) {
      setTimeout(() => {
        const baseExternalEventId = getBaseExternalEventId(this.props.eventParent.externalEventId);
        track(TrackingEvents.DEFRAG.ENABLE_AUTOPILOT_FROM_URL, {
          externalEventId: baseExternalEventId,
        });

        const suggestedStart =
          (event.suggestedAutopilotSettingsResponseV3.__typename ===
            "SuggestedAutopilotSettingsResponseV3" &&
            event.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings
              .allowedStartTime) ||
          null;

        const suggestedEnd =
          (event.suggestedAutopilotSettingsResponseV3.__typename ===
            "SuggestedAutopilotSettingsResponseV3" &&
            event.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings.allowedEndTime) ||
          null;

        const defaultFlex =
          (event.suggestedAutopilotSettingsResponseV3.__typename ===
            "SuggestedAutopilotSettingsResponseV3" &&
            ((event.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings
              .movementType as unknown) as EFlex)) ||
          EFlex.Day;

        allowSimilarRooms =
          allowSimilarRooms !== null && allowSimilarRooms !== undefined
            ? allowSimilarRooms
            : event.suggestedAutopilotSettingsResponseV3.__typename ===
              "SuggestedAutopilotSettingsResponseV3"
            ? event.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings.isRoomFlexible
            : false;

        const setMoveRange = getSetMoveRange(event);

        const allowedStart = (setMoveRange?.allowedStartTime || suggestedStart) ?? null;
        const allowedEnd = (setMoveRange?.allowedEndTime || suggestedEnd) ?? null;

        this.onFormChange({
          timeRange: {
            allowedStart,
            allowedEnd,
          },
          flex: setTimeFlexOnMount ? setTimeFlexOnMount : enableOnMount ? defaultFlex : flex,
          allowSimilarRooms:
            setRoomFlexOnMount !== undefined ? setRoomFlexOnMount : allowSimilarRooms,
        });

        const newPath = window.location.href
          .replace(/[?&]apenable/, "")
          .replace(/[?&]apflexday/, "")
          .replace(/[?&]apflexweek/, "")
          .replace(/[?&]apflexroom/, "");
        window.history.replaceState({ path: newPath }, "", newPath);
      }, 500);
    }
  }

  public componentDidUpdate() {
    this.sendEventAutopilotStatus(this.props, this.state.flex, !!this.state.allowSimilarRooms);
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private sendEventAutopilotStatus = (props: IProps, flex: string, allowSimilarRooms: boolean) => {
    const { event, calendarId, canAutopilot } = props;
    const hasRoom = eventHasRoom(event);
    const autopilotEventStatus = getValue(event.autopilotEventStatus);
    PostMessageManager.sendEventAutopilotStatus(
      event.eventKey.externalEventId,
      calendarId,
      flex,
      allowSimilarRooms,
      hasRoom,
      !canAutopilot,
      !!event?.annotatedEvent?.orgTags?.find(
        (tag) => tag.tag === "MovementPaused" && tag.state.active,
      ),
      autopilotEventStatus?.status || "",
      this.props.attendeeNotInDomain,
      this.props.organizerNotClockwiseUser,
      this.props.isAllDayEvent,
    );
  };

  private getCalendarIdsFromEvent(event: ISchema.IEvent) {
    const calendarIds = [];

    const organizer = event.organizer;

    if (organizer && organizer.urnValue) {
      calendarIds.push(organizer.urnValue);
    }

    return calendarIds;
  }

  private getUserIdsFromTagState(tagState?: TagState) {
    const userIds = [];

    if (tagState && tagState.subjectType === SubjectTypeEnum.User) {
      userIds.push(tagState.subjectValue);
    }

    return userIds;
  }

  private getMovementTagStateFromEvent(event: ISchema.IEvent): TagState | undefined {
    return getTagState(event, Tags.MovementType, true);
  }

  private getEnabledByText(
    personsByUserIdsErrorable?: ISchema.PersonListErrorable,
  ): string | undefined {
    const tagState = this.getMovementTagStateFromEvent(this.props.event);
    let enabledByText;

    if (
      tagState &&
      personsByUserIdsErrorable &&
      personsByUserIdsErrorable.__typename === "PersonList" &&
      personsByUserIdsErrorable.list.length
    ) {
      const person = personsByUserIdsErrorable.list[0];
      const startTime = new ZonedMoment(tagState.lastModified);
      enabledByText = `${
        tagState.value === EFlex.Pin ? "Disabled" : "Enabled"
      } by ${niceNameFromPerson(person, true)}`;

      const now = new ZonedMoment();
      if (now.valueOf() > startTime.valueOf()) {
        const startAgo = startTime.fromNow();
        enabledByText = `${enabledByText} ${startAgo}`;
      }
    }

    return enabledByText;
  }

  // ~-~-~-~-~-~-~-
  // State Management
  // ~-~-~-~-~-~-~-
  private getStateFromProps = (props: IProps): IState => {
    const { event } = props;

    const flex = getFlexFromEvent(event, true);
    // if allow similar rooms is null, it hasn't been set yet
    const allowSimilarRooms = getSimilarRoomsOkFromEvent(event, true);
    let suggestedStart: ISchema.IDayOffsetTimeSlot | null = null;
    let suggestedEnd: ISchema.IDayOffsetTimeSlot | null = null;
    let suggestedRoomFlex = false;

    const hasMovementTypeBeenSet = isAutopilotExplicitlyEnabled(event);

    if (!hasMovementTypeBeenSet) {
      // if movement type has never been set, initialize with smart suggestions
      const suggestedAutopilotSettingsResponse =
        event.suggestedAutopilotSettingsResponseV3.__typename ===
          "SuggestedAutopilotSettingsResponseV3" && event.suggestedAutopilotSettingsResponseV3;

      suggestedStart =
        (suggestedAutopilotSettingsResponse &&
          suggestedAutopilotSettingsResponse.suggestedAutopilotSettings.allowedStartTime) ||
        null;

      suggestedEnd =
        (event.suggestedAutopilotSettingsResponseV3.__typename ===
          "SuggestedAutopilotSettingsResponseV3" &&
          event.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings.allowedEndTime) ||
        null;

      suggestedRoomFlex =
        event.suggestedAutopilotSettingsResponseV3.__typename ===
        "SuggestedAutopilotSettingsResponseV3"
          ? event.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings.isRoomFlexible
          : false;
    }

    const moveRange = getSetMoveRange(event);

    const allowedStart = (moveRange?.allowedStartTime || suggestedStart) ?? null;
    const allowedEnd = (moveRange?.allowedEndTime || suggestedEnd) ?? null;

    return {
      allowedStart,
      allowedEnd,
      flex,
      allowSimilarRooms: allowSimilarRooms || suggestedRoomFlex,
      isSaving: false,
    };
  };

  // ~-~-~-~-~-~-~-
  // Handlers
  // ~-~-~-~-~-~-~-
  private onFormChange = (formState: IAutopilotSuggestionOptions) => {
    const { flex, allowSimilarRooms, timeRange } = formState;
    this.setState({ flex, allowSimilarRooms, ...timeRange }, this.save);

    const baseExternalEventId = getBaseExternalEventId(this.props.eventParent.externalEventId);
    track(TrackingEvents.DEFRAG.TOGGLE_AUTOPILOT_SETTING, {
      ...timeRange,
      flex,
      allowSimilarRooms,
      externalEventId: baseExternalEventId,
    });
  };

  // ~-~-~-~-~-~-~-
  // Saving
  // ~-~-~-~-~-~-~-
  // success callback after mutation
  private onSaveSuccess = () => {
    const t = this.context;
    this.setState({ isSaving: false });
    this.props.snackBarOpen(
      this.state.flex === EFlex.Pin
        ? (t("autopilot-form:disabled") as string)
        : (t("autopilot-form:updated") as string),
    );
    this.sendEventAutopilotStatus(this.props, this.state.flex, !!this.state.allowSimilarRooms); // on success, we are good
  };

  // error callback after mutation
  private onError = () => {
    this.setState({ isSaving: false });
    this.props.snackBarOpen("Whoops! Please try again.");
    const { event } = this.props;
    const flex = getFlexFromEvent(event, true);
    const allowSimilarRooms = getSimilarRoomsOkFromEvent(event);
    this.sendEventAutopilotStatus(this.props, flex, !!allowSimilarRooms); // on fail, post message old ap info back so toggles match
  };

  // take the form state and update the relevant autopilot tags
  private save = () => {
    const { eventParent, org, relay, calendarId } = this.props;
    const { flex, allowSimilarRooms, allowedStart, allowedEnd } = this.state;

    this.setState({ flex, allowSimilarRooms, isSaving: true });

    // construct the tags we'll add or remove
    const saveTags = generateEventFormSaveTags(flex, false, false, allowSimilarRooms);

    // add the org id and eventparent id to the input
    const input = {
      calendarId,
      orgRelayId: org.id,
      eventParentRelayId: eventParent.id,
      moveRangeV3: {
        allowedStartTimeInclusive: allowedStart!,
        allowedEndTimeInclusive: allowedEnd!,
      },
      ...saveTags,
    } as ISchema.IAddAndRemoveOrgTagsMutationInput;

    track(TrackingEvents.DEFRAG.COMPLETE_ANNOTATION_SUGGESTION, {
      allowSimilarRooms,
      externalEventId: eventParent.externalEventId,
      flexibility: flex,
    });

    addAndRemoveOrgTags(
      relay.environment,
      input,
      this.onSaveSuccess,
      this.onError,
      "APFormSaveOnEdit - save",
    );
  };

  public unpauseEvent = () => {
    const { event, org, calendarId } = this.props;

    unpauseEvent(
      this.props.relay.environment,
      { orgRelayId: org.id, externalEventId: event.eventKey.externalEventId },
      () => null,
      () => null,
      calendarId,
    );

    track(TrackingEvents.DEFRAG.UNPAUSE_AUTOPILOT, {
      externalEventId: event.eventKey.externalEventId,
    });
  };

  // ~-~-~-~-~-~-~-
  // Rendering
  // ~-~-~-~-~-~-~-

  public render() {
    const { event, org } = this.props;

    const attendeePeople = getValue(event?.attendeePeople);
    const organizerForDisplay = getOrganizerNameAndEmailFromAttendeeList(
      event.organizer.urnValue,
      attendeePeople,
    );

    const warning = maybeGetWarningType(
      this.props.event,
      this.props.canAutopilot,
      this.props.attendeeNotInDomain,
      this.props.organizerNotClockwiseUser,
      this.props.isAllDayEvent,
    );
    const hasRoom = eventHasRoom(this.props.event);
    const formState = {
      flex: this.state.flex,
      allowSimilarRooms: this.state.allowSimilarRooms,
      timeRange: {
        allowedStart: this.state.allowedStart,
        allowedEnd: this.state.allowedEnd,
      },
    } as IAutopilotSuggestionOptions;

    const enabledByText = this.getEnabledByText(this.props.org.personsByUserIdsErrorable);

    return (
      <React.Fragment>
        <TutorialSetMeetingFlexibility userWebAppFlags={this.props.user.webAppFlagsErrorable} />
        <AutopilotForm
          org={org}
          event={event}
          unpauseEvent={this.unpauseEvent}
          enabledByText={enabledByText}
          organizerForDisplay={organizerForDisplay}
          isDisabled={this.state.isSaving || !this.props.canReschedule}
          showRoom={hasRoom}
          formState={formState}
          onChange={this.onFormChange}
          warning={warning}
          style={{ cursor: "pointer" }} // dont show the disabled cursor
        />
      </React.Fragment>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    activeViewEvent: state.webExtension.activeViewEvent,
    setEventTags: state.webExtension.setEventTags,
    mode: state.webExtension.mode,
    contentScrollTop: state.webExtension.contentScrollTop,
    currentTip: state.weekView.currentTip,
  };
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
  setCurrentTip,
})(AutopilotFormSaveOnEditCmpt);

//////////////////
// RELAY
//////////////////
export const AutopilotFormSaveOnEdit = createRefetchContainer<IContainer>(
  reduxContainer,
  autopilotFormSaveOnEditFragments,
  refetchQuery,
);
